import { useEffect, useRef } from 'react';
import { datadogLogs } from '@datadog/browser-logs';
import { useAuth } from '../../contexts/auth-context';
import { useGlobalUIStore } from '../../stores/global-ui-store';

// Extract the content hash from the entry bundle script tag at page load.
// This is compared against version.json to detect when a new deployment has occurred.
// Matches both webpack (main.abc123.js) and Vite/Rolldown (index.BRt9fdnc.js) patterns.
// Rolldown uses base62 hashes ([a-zA-Z0-9]), webpack used hex ([a-f0-9]).
const hash_regex = /(?:main|index)\.([a-zA-Z0-9]+)\.js$/;
const matching_script = Array.from(document.getElementsByTagName('script') || []).find((script) =>
  hash_regex.test(script.src)
);
const current_hash = matching_script ? (matching_script.src.match(hash_regex) || [])[1] : null;

interface VersionData {
  hash?: { main?: string };
  build?: string;
}

const fetchVersionData = async (): Promise<VersionData | undefined> => {
  try {
    const response = await fetch(`/version.json?t=${new Date().getTime()}`);
    const data = (await response.json()) as VersionData;
    return data;
  } catch {
    // Silently ignore fetch errors — version check is non-critical
  }
};

/**
 * Compares the fetched version data against the currently loaded bundle hash.
 * Extracted to module level because the React Compiler cannot analyze optional
 * chaining inside try/catch blocks ("Support value blocks within a try/catch
 * statement" bail-out). Module-level functions are outside the compiler's scope.
 */
function processVersionResponse(response: VersionData | undefined): void {
  const latest_app_version = response?.hash?.main ?? 'local env';
  const loaded_app_version = current_hash ?? 'local env';
  const latest_app_build_number = response?.build ?? 'local env';

  const hash_changed = response
    ? Boolean(current_hash && current_hash !== response.hash?.main)
    : false;

  // Only log when a new version is detected — logging every 60s for matching
  // versions wastes Datadog budget (~1,440 events/user/day for zero signal).
  // Per logging.md rule 5: "Log unhappy paths, not happy paths."
  if (hash_changed) {
    datadogLogs.logger.info('New app version detected:', {
      current_loaded_app_info: {
        loaded_app_version,
        latest_app_version,
        latest_app_build_number,
      },
    });
    useGlobalUIStore.getState().setHasNewVersion(true);
  }
}

/**
 * Headless component that polls /version.json every 60s (after an initial 1s delay)
 * and sets hasNewVersion in the global UI store when a new deployment is detected.
 * Gated on auth ready state — only starts after authentication is complete.
 */
export function VersionCheck() {
  const { ready } = useAuth();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!ready) return;

    const checkVersion = () => {
      fetchVersionData()
        .then((response) => {
          processVersionResponse(response);
        })
        .catch(() => {
          // Silently ignore — version check is non-critical
        });
    };

    // Initial check after 1s delay, then every 60s thereafter
    timeoutRef.current = setTimeout(() => {
      checkVersion();
      intervalRef.current = setInterval(checkVersion, 60_000);
    }, 1000);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [ready]);

  return null;
}
